import { useMemo } from 'react';

import { getFragment } from '../graphql/client/helpers/get-fragment';
import type {
    AuthorizedMedicationInfoFragment,
    DigitallyReceivedPrescriberOrderInfoFragment,
    MedicationInfoFragment,
    UnclaimedPrescriberOrderInfoFragment,
    UnknownMedicationInfoFragment,
} from '../graphql/generated/graphql';

export type OrderMedicationType =
    | DigitallyReceivedPrescriberOrderInfoFragment
    | UnclaimedPrescriberOrderInfoFragment
    | undefined;

export const useOrderMedication = (
    prescriberOrder: OrderMedicationType,
    whichMedication: 'prescribed' | 'authorized' = 'prescribed'
) =>
    useMemo(() => {
        if (!prescriberOrder) {
            return undefined;
        }

        if (prescriberOrder.__typename !== 'DigitallyReceivedPrescriberOrder') {
            return undefined;
        }

        return whichMedication === 'prescribed'
            ? getFragment(prescriberOrder.prescribedMedication)
            : getFragment(prescriberOrder.authorizedMedication);
    }, [prescriberOrder, whichMedication]);

export const usePrescribedMedication = (prescriberOrder: OrderMedicationType) =>
    useOrderMedication(prescriberOrder, 'prescribed') as
        | MedicationInfoFragment
        | UnknownMedicationInfoFragment
        | undefined;

export const useAuthorizedMedication = (prescriberOrder: OrderMedicationType) =>
    useOrderMedication(prescriberOrder, 'authorized') as
        | AuthorizedMedicationInfoFragment
        | undefined;
